// src/pages/agent/SubmitLead.js
// Lead-submitter view, paste-first edition.
// The whole flow is: copy from Zillow / Redfin / Realtor → paste here →
// AI auto-fills every required field → submitter reviews → submits.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { PageHeader, Card, Btn, Field, Input, TextArea, BORDER, BURG, BURG_DK, GOLD, GOLD_PALE, CREAM, MID, LIGHT } from '../../components/agent/UI';
import AddressAutocomplete from '../../components/agent/AddressAutocomplete';
import { findDuplicateLead, DuplicateLeadModal } from '../../lib/leadDedupe';
import { checkOptOut, OptOutWarningModal } from '../../lib/optOutCheck';

export default function SubmitLead() {
  const { profile } = useAgentAuth();
  const navigate = useNavigate();
  const [adminUid, setAdminUid] = useState(profile?.invitedByUid || null);
  const [data, setData] = useState(blankLead());
  const [pasteText, setPasteText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [savedFlash, setSavedFlash] = useState('');
  const [clipboardErr, setClipboardErr] = useState('');
  const [duplicate, setDuplicate] = useState(null);
  const [pendingSubmit, setPendingSubmit] = useState(null); // remembers options if user overrides
  const [optOut, setOptOut] = useState(null);
  const [optOutOverridden, setOptOutOverridden] = useState(false);
  const autoParseTimer = useRef(null);

  // Resolve which admin to send leads to.
  useEffect(() => {
    if (adminUid) return;
    (async () => {
      try {
        const q = query(collection(db, 'agents'), where('role', '==', 'admin'), where('status', '==', 'active'));
        const snap = await getDocs(q);
        if (!snap.empty) setAdminUid(snap.docs[0].id);
      } catch {}
    })();
  }, [adminUid]);

  const setOwner = (k, v) => setData(d => ({ ...d, owner: { ...d.owner, [k]: v } }));
  const setAddr  = (k, v) => setData(d => ({ ...d, address: { ...d.address, [k]: v } }));

  // Read from clipboard with one click (modern browsers, requires user gesture).
  const pasteFromClipboard = async () => {
    setClipboardErr('');
    try {
      if (!navigator.clipboard?.readText) throw new Error('Browser does not support clipboard read.');
      const text = await navigator.clipboard.readText();
      if (!text || text.trim().length < 20) {
        setClipboardErr('Clipboard is empty. Copy the listing first, then click again.');
        return;
      }
      setPasteText(text);
      // Trigger parse right away on a clipboard click — no waiting.
      runParse(text);
    } catch (e) {
      setClipboardErr(`Couldn't read clipboard: ${e.message}. Just right-click → Paste in the box below instead.`);
    }
  };

  // When the user actually pastes into the textarea, parse automatically
  // after a short debounce. They don't have to click anything.
  const onPasteIntoArea = (e) => {
    const text = (e.clipboardData || window.clipboardData)?.getData('text') || '';
    if (text && text.length >= 20) {
      // Defer until React has set state.
      setTimeout(() => runParse(text), 0);
    }
  };

  // Manual change (typing in the textarea) — debounced auto-parse on substantial content.
  const onPasteTextChange = (val) => {
    setPasteText(val);
    if (autoParseTimer.current) clearTimeout(autoParseTimer.current);
    if (val.trim().length >= 80 && !parsed) {
      autoParseTimer.current = setTimeout(() => runParse(val), 1100);
    }
  };

  const runParse = async (overrideText) => {
    const text = overrideText ?? pasteText;
    if (!text || text.trim().length < 20) {
      setErr('Paste a chunk of listing details first.');
      return;
    }
    setErr(''); setParsing(true);
    try {
      const fn = httpsCallable(functions, 'parseListingText');
      // Send any URL we can sniff out so the parser gets full context.
      const sniffed = sniffListingUrl(text) || data.listingUrl;
      const res = await fn({ url: sniffed || '', text });
      if (!res.data?.ok) throw new Error(res.data?.message || 'Parse failed.');
      const p = res.data.parsed || {};
      setData(d => ({
        ...d,
        address: {
          line1: p.address?.line1 || d.address.line1,
          city:  p.address?.city  || d.address.city,
          state: p.address?.state || d.address.state,
          zip:   p.address?.zip   || d.address.zip
        },
        owner: {
          name:  p.owners?.[0]?.name  || d.owner.name,
          phone: p.owners?.[0]?.phone ? formatPhone(p.owners[0].phone) : d.owner.phone,
          email: p.owners?.[0]?.email || d.owner.email
        },
        listingUrl: p.listingUrl || sniffed || d.listingUrl,
        askingPrice: p.askingPrice ?? d.askingPrice,
        beds: p.beds ?? d.beds,
        baths: p.baths ?? d.baths,
        sqft: p.sqft ?? d.sqft
      }));
      setParsed(true);
      setSavedFlash('✨ Pulled the details. Review below and submit.');
      setTimeout(() => setSavedFlash(''), 4500);
    } catch (e) {
      setErr(`Parse error: ${e.message}`);
    } finally {
      setParsing(false);
    }
  };

  const validate = () => {
    if (!data.owner.phone.trim()) return 'Owner phone is required.';
    if (!data.listingUrl.trim()) return 'Listing URL is required.';
    if (!data.address.line1.trim()) return 'Property address is required.';
    return '';
  };

  const submit = async ({ andAddAnother, skipDupeCheck = false } = {}) => {
    const v = validate();
    if (v) { setErr(v); return; }
    if (!adminUid) { setErr('No admin found to receive your lead. Please contact your admin.'); return; }

    // Opt-out gate — refuse to add a seller who texted STOP.
    if (!optOutOverridden) {
      const opt = await checkOptOut(data.owner.phone);
      if (opt) { setOptOut(opt); setPendingSubmit({ andAddAnother }); return; }
    }

    // Failsafe: refuse to enter the same property twice. Custom modal,
    // never a browser alert.
    if (!skipDupeCheck) {
      const dupe = await findDuplicateLead({
        address: data.address,
        listingUrl: data.listingUrl
      });
      if (dupe) {
        setDuplicate(dupe);
        setPendingSubmit({ andAddAnother });
        return;
      }
    }

    setErr(''); setSaving(true);
    try {
      const payload = {
        address: data.address,
        owners: [{
          name: (data.owner.name || 'Property Owner').trim(),
          phone: data.owner.phone.trim(),
          email: data.owner.email.trim()
        }],
        listingUrl: data.listingUrl.trim(),
        propertyData: {
          askingPrice: data.askingPrice ? Number(data.askingPrice) : null,
          beds: data.beds ? Number(data.beds) : null,
          baths: data.baths ? Number(data.baths) : null,
          sqft: data.sqft ? Number(data.sqft) : null
        },
        status: 'New',
        notes: [],
        contracts: [],
        smsThread: [],
        calc: null,
        submittedBy: {
          uid: profile.uid,
          name: profile.name || profile.email,
          email: profile.email
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: profile.uid
      };
      await addDoc(collection(db, 'agents', adminUid, 'leads'), payload);
      setData(blankLead());
      setPasteText('');
      setParsed(false);
      setSavedFlash('✓ Lead submitted. Thanks!');
      setTimeout(() => setSavedFlash(''), 4000);
      if (!andAddAnother) navigate('/agent/submissions');
    } catch (e) {
      setErr(`Couldn't submit: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const clearAll = () => {
    setData(blankLead()); setPasteText(''); setParsed(false); setErr('');
  };

  // Confirmation: do the URL slug and the address roughly agree?
  const matchInfo = useMemo(() => verifyAddressMatchesUrl(data.address, data.listingUrl), [data.address, data.listingUrl]);

  return (
    <>
      <PageHeader
        eyebrow="Submit a Lead"
        title={<>Paste from <em style={{ fontStyle: 'italic', color: BURG }}>Zillow</em>. We'll do the rest.</>}
        sub="Copy the listing on Zillow, Redfin, Realtor, FSBO, or Craigslist — then paste here. AI fills the address, beds, baths, price, owner, and phone for you."
      />

      {/* ── Step 1: paste ─────────────────────────────────── */}
      <Card title="① Paste the listing" right={parsed && <span style={ss.parsedBadge}>✓ Parsed</span>}>
        <div style={ss.heroBlock}>
          <div style={ss.heroSteps}>
            <div style={ss.heroStep}><strong style={{ color: BURG }}>1.</strong> Open the listing on Zillow / Redfin / Realtor.</div>
            <div style={ss.heroStep}><strong style={{ color: BURG }}>2.</strong> <kbd style={ss.kbd}>⌘A</kbd> (or <kbd style={ss.kbd}>Ctrl+A</kbd>) → <kbd style={ss.kbd}>⌘C</kbd> to copy.</div>
            <div style={ss.heroStep}><strong style={{ color: BURG }}>3.</strong> Click the gold button below — or right-click → Paste in the box.</div>
          </div>
          <Btn variant="gold" type="button" onClick={pasteFromClipboard} disabled={parsing} style={{ width: '100%', padding: '14px 20px', fontSize: 14 }}>
            {parsing ? 'Reading & parsing…' : '📋 Paste from clipboard'}
          </Btn>
          {clipboardErr && <div style={ss.clipNote}>{clipboardErr}</div>}
        </div>

        <div style={{ height: 14 }} />

        <Field
          label="Or paste here manually"
          hint="Right-click → Paste, or ⌘V / Ctrl+V. We auto-parse the moment you paste."
        >
          <TextArea
            rows={6}
            value={pasteText}
            onPaste={onPasteIntoArea}
            onChange={e => onPasteTextChange(e.target.value)}
            placeholder="Paste anything from the listing here — address, price, beds, baths, phone, description… we figure it out."
            style={{ minHeight: 130 }}
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 10, flexWrap: 'wrap' }}>
          <div style={ss.tip}>💡 Tip: Zillow's "For sale by owner" listings have the phone right on the page — that's the gold mine.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(pasteText || parsed) && <Btn type="button" variant="ghost" onClick={clearAll}>Clear</Btn>}
            {pasteText && !parsed && (
              <Btn type="button" variant="gold" onClick={() => runParse()} disabled={parsing}>
                {parsing ? 'Parsing…' : '✨ Parse now'}
              </Btn>
            )}
          </div>
        </div>
      </Card>

      {/* ── Step 2: review ─────────────────────────────────── */}
      <Card
        title="② Review and submit"
        right={parsed && matchInfo.label && (
          <span style={matchInfo.ok ? ss.matchOk : ss.matchWarn}>{matchInfo.label}</span>
        )}
      >
        <div style={ss.reviewIntro}>
          {parsed
            ? 'Double-check the auto-filled fields below — fix anything that looks off, then submit.'
            : 'Paste a listing above, or fill these in manually if you have them on hand.'}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Owner phone" required hint="The number to call back. Auto-formatted.">
            <Input value={data.owner.phone} onChange={e => setOwner('phone', formatPhone(e.target.value))} placeholder="(555) 123-4567" inputMode="tel" />
          </Field>
          <Field label="Owner name" hint='Optional — leave blank if not shown on the listing. Defaults to "Property Owner".'>
            <Input value={data.owner.name} onChange={e => setOwner('name', e.target.value)} placeholder="Often not shown — that's fine" />
          </Field>
        </div>
        <Field label="Listing URL" required>
          <Input type="url" value={data.listingUrl} onChange={e => setData(d => ({ ...d, listingUrl: e.target.value }))} placeholder="https://www.zillow.com/homedetails/..." />
        </Field>
        <Field label="Street address" required hint="Start typing — Google fills city/state/zip.">
          <AddressAutocomplete
            value={data.address.line1}
            onChange={e => setAddr('line1', e.target.value)}
            onSelect={(parts) => setData(d => ({
              ...d,
              address: {
                line1: parts.line1 || d.address.line1,
                city: parts.city || d.address.city,
                state: parts.state || d.address.state,
                zip: parts.zip || d.address.zip
              }
            }))}
            placeholder="2847 Thornwood Lane"
          />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
          <Field label="City"><Input value={data.address.city} onChange={e => setAddr('city', e.target.value)} /></Field>
          <Field label="State"><Input value={data.address.state} onChange={e => setAddr('state', e.target.value.toUpperCase().slice(0,2))} maxLength={2} /></Field>
          <Field label="Zip"><Input value={data.address.zip} onChange={e => setAddr('zip', e.target.value)} /></Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 12 }}>
          <Field label="Asking price ($)"><Input type="number" value={data.askingPrice} onChange={e => setData(d => ({ ...d, askingPrice: e.target.value }))} placeholder="175000" /></Field>
          <Field label="Beds"><Input type="number" value={data.beds} onChange={e => setData(d => ({ ...d, beds: e.target.value }))} placeholder="3" /></Field>
          <Field label="Baths"><Input type="number" step="0.5" value={data.baths} onChange={e => setData(d => ({ ...d, baths: e.target.value }))} placeholder="2" /></Field>
          <Field label="Sq Ft"><Input type="number" value={data.sqft} onChange={e => setData(d => ({ ...d, sqft: e.target.value }))} placeholder="1920" /></Field>
        </div>
        <Field label="Email (optional)">
          <Input type="email" value={data.owner.email} onChange={e => setOwner('email', e.target.value)} placeholder="jane@example.com" />
        </Field>
      </Card>

      {err && <div style={ss.err}>{err}</div>}
      {savedFlash && <div style={ss.success}>{savedFlash}</div>}

      <div style={ss.actions}>
        <Btn type="button" variant="ghost" onClick={() => submit({ andAddAnother: true })} disabled={saving}>
          {saving ? 'Submitting…' : '+ Submit & Add Another'}
        </Btn>
        <Btn type="button" variant="gold" onClick={() => submit({ andAddAnother: false })} disabled={saving}>
          {saving ? 'Submitting…' : 'Submit Lead →'}
        </Btn>
      </div>

      {optOut && (
        <OptOutWarningModal
          optOut={optOut}
          isAdmin={profile?.role === 'admin'}
          onCancel={() => { setOptOut(null); setPendingSubmit(null); }}
          onOverride={async (confirmation) => {
            try {
              const fn = httpsCallable(functions, 'clearOptOut');
              await fn({ phone: optOut.phone, confirmation });
              setOptOutOverridden(true);
              setOptOut(null);
              // Retry the submit with the opts we stashed.
              const opts = pendingSubmit || { andAddAnother: false };
              setPendingSubmit(null);
              submit({ ...opts });
            } catch (e) {
              setErr(`Couldn't clear opt-out: ${e.message}`);
            }
          }}
        />
      )}

      {duplicate && (
        <DuplicateLeadModal
          duplicate={duplicate}
          onClose={() => { setDuplicate(null); setPendingSubmit(null); }}
          onView={(d) => {
            setDuplicate(null);
            setPendingSubmit(null);
            // Lead submitters see their submission detail; admins/agents go straight to the lead.
            if (profile?.role === 'lead_submitter') {
              navigate(`/agent/submissions/${d.id}`);
            } else {
              navigate(`/agent/leads/${d.id}`);
            }
          }}
          onSaveAnyway={() => {
            const opts = pendingSubmit || { andAddAnother: false };
            setDuplicate(null);
            setPendingSubmit(null);
            submit({ ...opts, skipDupeCheck: true });
          }}
        />
      )}
    </>
  );
}

function blankLead() {
  return {
    address: { line1: '', city: '', state: '', zip: '' },
    owner: { name: '', phone: '', email: '' },
    listingUrl: '',
    askingPrice: '',
    beds: '', baths: '', sqft: ''
  };
}

function formatPhone(v) {
  const d = (v || '').replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

// Best-effort URL detection in messy pasted text. Looks for the first http(s)
// URL on a real-estate domain we recognize.
function sniffListingUrl(text) {
  if (!text) return '';
  const urls = (text.match(/https?:\/\/[^\s)]+/gi) || [])
    .map(u => u.replace(/[.,;]+$/, ''));
  if (!urls.length) return '';
  const preferred = urls.find(u => /(zillow|redfin|realtor|trulia|homes|craigslist|fsbo|loopnet|landwatch)\./i.test(u));
  return preferred || urls[0];
}

// Light sanity check: does the URL slug roughly contain pieces of the address?
// Returns { ok, label }. Used as a confirmation badge — not a hard block.
function verifyAddressMatchesUrl(address, url) {
  if (!url || !address?.line1) return { ok: false, label: '' };
  const slug = url.toLowerCase();
  const street = (address.line1 || '').toLowerCase().split(/\s+/).filter(Boolean);
  const city = (address.city || '').toLowerCase();
  const stateAbbr = (address.state || '').toLowerCase();
  // Need a street-number AND at least one street word in the URL slug to count.
  const numberInSlug = street[0] && /^\d+$/.test(street[0]) && slug.includes(street[0]);
  const wordInSlug = street.slice(1).some(w => w.length >= 3 && slug.includes(w));
  const cityInSlug = city && slug.includes(city.replace(/\s+/g, '-'));
  const stateInSlug = stateAbbr && slug.includes(`-${stateAbbr}-`);
  const match = (numberInSlug && wordInSlug) || (cityInSlug && stateInSlug);
  return match
    ? { ok: true, label: '✓ URL matches address' }
    : { ok: false, label: '⚠ Double-check the URL matches the address' };
}

const ss = {
  err: { padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 4, color: '#991B1B', fontSize: 12, marginBottom: 14 },
  success: { padding: '10px 14px', background: '#E7F5EE', border: '1px solid rgba(15,107,63,0.25)', borderRadius: 4, color: '#0F6B3F', fontSize: 12, marginBottom: 14 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 },

  heroBlock: { background: GOLD_PALE, border: `1px solid rgba(201,168,76,0.4)`, borderRadius: 10, padding: '16px 18px' },
  heroSteps: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 },
  heroStep: { fontSize: 13, color: BURG_DK, lineHeight: 1.6 },
  kbd: { display: 'inline-block', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 4, padding: '1px 6px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11, color: BURG },
  clipNote: { marginTop: 10, padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 4, color: '#991B1B', fontSize: 12 },
  tip: { fontSize: 12, color: MID, fontStyle: 'italic', flex: 1, minWidth: 220 },

  parsedBadge: { fontSize: 11, color: '#0F6B3F', background: '#E7F5EE', border: '1px solid rgba(15,107,63,0.3)', padding: '3px 10px', borderRadius: 999, fontWeight: 500, letterSpacing: '0.04em' },
  matchOk: { fontSize: 11, color: '#0F6B3F', background: '#E7F5EE', border: '1px solid rgba(15,107,63,0.3)', padding: '3px 10px', borderRadius: 999 },
  matchWarn: { fontSize: 11, color: '#92400E', background: '#FEF3C7', border: '1px solid #FCD34D', padding: '3px 10px', borderRadius: 999 },

  reviewIntro: { fontSize: 13, color: MID, lineHeight: 1.65, marginBottom: 14 }
};
